import { cn, cva, NoddiIcon, type VariantProps } from 'noddi-ui-common';
import type { ButtonHTMLAttributes } from 'react';

const tileVariants = cva(
  'relative flex w-full text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-card-selected-background/40 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        small: 'rounded-lg border-2 p-3',
        medium: 'rounded-xl border-2 p-4',
        large: 'rounded-xl border-2 p-5'
      },
      align: {
        start: 'items-start',
        center: 'items-center'
      },
      selected: {
        true: 'border-card-selected-background bg-card-light-background',
        false: 'border-border bg-card hover:border-card-selected-background hover:bg-card-light-background/50'
      }
    },
    defaultVariants: {
      size: 'medium',
      align: 'start',
      selected: false
    }
  }
);

type TileVariants = VariantProps<typeof tileVariants>;

export type NoddiSelectableTileProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> &
  Omit<TileVariants, 'selected'> & {
    selected: boolean;
    showCheckmark?: boolean;
  };

export const NoddiSelectableTile = ({
  selected,
  showCheckmark = true,
  size,
  align,
  className,
  children,
  ...buttonProps
}: NoddiSelectableTileProps) => (
  <button
    type='button'
    data-slot='selectable-tile'
    aria-pressed={selected}
    className={cn(tileVariants({ size, align, selected }), className)}
    {...buttonProps}
  >
    {children}
    {showCheckmark && selected && (
      <span className='absolute top-3 right-3 flex size-5 items-center justify-center rounded-full bg-card-selected-background text-card-selected-foreground'>
        <NoddiIcon name='Check' size='xSmall' />
      </span>
    )}
  </button>
);

NoddiSelectableTile.displayName = 'NoddiSelectableTile';
